import { useEffect, useState, type FC } from "react";
import { Checkbox, Label, TextInput, Tooltip } from "flowbite-react";
import { HiOutlineInformationCircle } from "react-icons/hi";
import { Dialog, Button } from "@components";
import { controlBrand } from "@design-system";
import { useTranslation } from "react-i18next";
import type { InvoiceDeliveryOptions } from "../../api/client";

const isValidEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

export interface SendInvoiceProp {
  show: boolean;
  isLoading: boolean;
  isResend?: boolean;
  initialIssuanceMode?: InvoiceDeliveryOptions["issuanceMode"];
  lockedIssuanceMode?: InvoiceDeliveryOptions["issuanceMode"];
  accountingCopyOnly?: boolean;
  recoveryMessage?: string;
  customerEmail?: string;
  accountingEmail?: string;
  defaultSendToAccounting?: boolean;
  integratedAccountingProvider?: "tripletex";
  accountingIntegrationLoading?: boolean;
  toggleModal: () => void;
  confirmButton: (options: InvoiceDeliveryOptions) => void;
}

export const SendInvoice: FC<SendInvoiceProp> = (props) => {
  const { t } = useTranslation(["invoice"]);
  return (
    <Dialog
      size="lg"
      denseHeader
      show={props.show}
      dismissible
      title={
        props.accountingCopyOnly
          ? t("actions.resend_accounting_copy_title")
          : props.isResend
            ? t("actions.resend_dialog_title")
            : t("actions.send_dialog_title")
      }
      toggleModal={props.toggleModal}
      hideButtons
      content={<DialogContent {...props} />}
    />
  );
};

const DialogContent: FC<SendInvoiceProp> = (props) => {
  const { t } = useTranslation(["common", "invoice", "validation"]);
  const [issuanceMode, setIssuanceMode] =
    useState<InvoiceDeliveryOptions["issuanceMode"]>("logibud");
  const [customerEmail, setCustomerEmail] = useState("");
  const [accountingEmail, setAccountingEmail] = useState("");
  const [sendToAccounting, setSendToAccounting] = useState(false);
  const [saveCustomerEmail, setSaveCustomerEmail] = useState(false);
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    if (!props.show) return;
    const initialIssuanceMode = props.initialIssuanceMode ?? "logibud";
    setIssuanceMode(initialIssuanceMode);
    setCustomerEmail(props.customerEmail ?? "");
    setAccountingEmail(props.accountingEmail ?? "");
    setSendToAccounting(
      initialIssuanceMode === "accounting_provider" ||
        props.accountingCopyOnly === true ||
        props.defaultSendToAccounting === true,
    );
    setSaveCustomerEmail(false);
    setValidationError("");
  }, [
    props.show,
    props.initialIssuanceMode,
    props.accountingCopyOnly,
    props.customerEmail,
    props.accountingEmail,
    props.defaultSendToAccounting,
  ]);

  const requiresAccountingEmail =
    (issuanceMode === "accounting_provider" &&
      !props.integratedAccountingProvider &&
      !props.accountingIntegrationLoading) ||
    (issuanceMode === "logibud" && sendToAccounting) ||
    props.accountingCopyOnly === true;

  const validateAndSend = () => {
    const sendToCustomer =
      issuanceMode === "logibud" && !props.accountingCopyOnly;
    if (sendToCustomer && !isValidEmail(customerEmail)) {
      setValidationError(t("invoice:actions.invalid_customer_email"));
      return;
    }
    if (requiresAccountingEmail && !isValidEmail(accountingEmail)) {
      setValidationError(t("invoice:actions.invalid_accounting_email"));
      return;
    }
    setValidationError("");
    props.confirmButton({
      issuanceMode,
      sendToCustomer,
      customerEmail: sendToCustomer ? customerEmail.trim() : undefined,
      sendToAccounting:
        issuanceMode === "accounting_provider"
          ? !props.integratedAccountingProvider
          : props.accountingCopyOnly === true
            ? true
            : sendToAccounting,
      accountingEmail: requiresAccountingEmail
        ? accountingEmail.trim()
        : undefined,
      saveCustomerEmail: sendToCustomer && saveCustomerEmail,
    });
  };

  return (
    <div className="flex flex-col gap-4">
      {props.recoveryMessage && (
        <div
          role="alert"
          className="rounded-lg bg-warning-muted p-3 text-sm leading-5 text-text"
        >
          {props.recoveryMessage}
        </div>
      )}
      {props.isResend && !props.recoveryMessage && (
        <div className="rounded-lg bg-surface-muted p-3 text-sm leading-5 text-text">
          {t(
            issuanceMode === "accounting_provider"
              ? "invoice:actions.resend_basis_dialog_description"
              : "invoice:actions.resend_dialog_description",
          )}
        </div>
      )}
      {!props.accountingCopyOnly && (
        <p className="text-sm text-muted">
          {t("invoice:actions.issuance_choice_description")}
        </p>
      )}

      {!props.accountingCopyOnly && (
        <label
          className={`block cursor-pointer rounded-lg border p-4 ${
            issuanceMode === "logibud"
              ? "border-brand bg-brand-muted/30"
              : "border-border-strong"
          } ${props.lockedIssuanceMode === "accounting_provider" ? "cursor-not-allowed opacity-60" : ""}`}
        >
          <div className="flex items-start gap-3">
            <input
              type="radio"
              name="invoice-delivery-method"
              value="logibud"
              checked={issuanceMode === "logibud"}
              disabled={props.lockedIssuanceMode === "accounting_provider"}
              onChange={() => setIssuanceMode("logibud")}
              className={`mt-1 h-4 w-4 shrink-0 text-brand ${controlBrand}`}
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 font-medium text-text">
                {t("invoice:actions.send_from_logibud")}
                <Tooltip
                  trigger="click"
                  content={t("invoice:actions.send_from_logibud_tooltip")}
                >
                  <HiOutlineInformationCircle
                    className="cursor-pointer text-muted"
                    size={18}
                  />
                </Tooltip>
              </div>
              <p className="mt-1 text-sm text-muted">
                {t("invoice:actions.send_from_logibud_description")}
              </p>
            </div>
          </div>

          {issuanceMode === "logibud" && (
            <div className="ml-7 mt-4 flex flex-col gap-3">
              <div>
                <Label
                  htmlFor="invoice-customer-email"
                  value={t("invoice:actions.customer_recipient")}
                />
                <TextInput
                  id="invoice-customer-email"
                  type="email"
                  value={customerEmail}
                  onChange={(event) => setCustomerEmail(event.target.value)}
                  className="mt-1 w-full"
                />
              </div>
              {customerEmail.trim() !== (props.customerEmail ?? "").trim() && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="save-customer-email"
                    checked={saveCustomerEmail}
                    className={`cursor-pointer ${controlBrand}`}
                    onChange={(event) =>
                      setSaveCustomerEmail(event.target.checked)
                    }
                  />
                  <Label
                    htmlFor="save-customer-email"
                    className="font-normal text-muted"
                  >
                    {t("invoice:actions.save_customer_email")}
                  </Label>
                </div>
              )}
              <div className="rounded-lg bg-surface-muted p-3">
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="send-accounting-copy"
                    checked={sendToAccounting}
                    className={`mt-1 shrink-0 cursor-pointer ${controlBrand}`}
                    onChange={(event) =>
                      setSendToAccounting(event.target.checked)
                    }
                  />
                  <div className="min-w-0 flex-1">
                    <Label
                      htmlFor="send-accounting-copy"
                      className="block cursor-pointer font-medium"
                    >
                      {t("invoice:actions.send_accounting_copy")}
                    </Label>
                    <p className="mt-1 text-sm leading-5 text-muted">
                      {t("invoice:actions.send_accounting_copy_description")}
                    </p>
                  </div>
                </div>
                {sendToAccounting && (
                  <TextInput
                    id="invoice-accounting-email"
                    type="email"
                    aria-label={t("invoice:actions.accounting_recipient")}
                    value={accountingEmail}
                    onChange={(event) => setAccountingEmail(event.target.value)}
                    className="ml-6 mt-2"
                    placeholder={t("invoice:actions.accounting_recipient")}
                  />
                )}
              </div>
            </div>
          )}
        </label>
      )}

      {!props.accountingCopyOnly && (
        <label
          className={`block cursor-pointer rounded-lg border p-4 ${
            issuanceMode === "accounting_provider"
              ? "border-brand bg-brand-muted/30"
              : "border-border-strong"
          } ${props.lockedIssuanceMode === "logibud" ? "cursor-not-allowed opacity-60" : ""}`}
        >
          <div className="flex items-start gap-3">
            <input
              type="radio"
              name="invoice-delivery-method"
              value="accounting"
              checked={issuanceMode === "accounting_provider"}
              disabled={props.lockedIssuanceMode === "logibud"}
              onChange={() => setIssuanceMode("accounting_provider")}
              className={`mt-1 h-4 w-4 shrink-0 text-brand ${controlBrand}`}
            />
            <div className="flex-1">
              <div className="font-medium text-text">
                {t(
                  props.accountingIntegrationLoading
                    ? "invoice:actions.checking_accounting_integration"
                    : props.integratedAccountingProvider === "tripletex"
                      ? "invoice:actions.create_in_tripletex"
                      : "invoice:actions.process_in_accounting",
                )}
              </div>
              <p className="mt-1 text-sm text-muted">
                {t(
                  props.accountingIntegrationLoading
                    ? "invoice:actions.checking_accounting_integration_description"
                    : props.integratedAccountingProvider === "tripletex"
                      ? "invoice:actions.create_in_tripletex_description"
                      : "invoice:actions.process_in_accounting_description",
                )}
              </p>
              {issuanceMode === "accounting_provider" && (
                <>
                  <div className="mt-3 rounded-lg bg-warning-muted p-3 text-sm text-text">
                    {t(
                      props.integratedAccountingProvider === "tripletex"
                        ? "invoice:actions.tripletex_customer_will_not_receive"
                        : "invoice:actions.customer_will_not_receive",
                    )}
                  </div>
                  {!props.integratedAccountingProvider &&
                    !props.accountingIntegrationLoading && (
                      <TextInput
                        id="invoice-accounting-email"
                        type="email"
                        aria-label={t("invoice:actions.accounting_recipient")}
                        value={accountingEmail}
                        onChange={(event) =>
                          setAccountingEmail(event.target.value)
                        }
                        className="mt-3"
                        placeholder={t("invoice:actions.accounting_recipient")}
                      />
                    )}
                </>
              )}
            </div>
          </div>
        </label>
      )}

      {props.accountingCopyOnly && (
        <div className="rounded-lg border border-brand bg-brand-muted/30 p-4">
          <Label
            htmlFor="invoice-accounting-copy-email"
            value={t("invoice:actions.accounting_recipient")}
          />
          <TextInput
            id="invoice-accounting-copy-email"
            type="email"
            value={accountingEmail}
            onChange={(event) => setAccountingEmail(event.target.value)}
            className="mt-2"
            placeholder={t("invoice:actions.accounting_recipient")}
          />
        </div>
      )}

      {validationError && (
        <p role="alert" className="text-sm text-red-600">
          {validationError}
        </p>
      )}

      <div className="mt-2 flex gap-4">
        <Button color="gray" onClick={props.toggleModal}>
          {t("common:cancel")}
        </Button>
        <Button
          onClick={validateAndSend}
          color="primary"
          disabled={
            props.isLoading ||
            (issuanceMode === "accounting_provider" &&
              props.accountingIntegrationLoading)
          }
        >
          {props.accountingCopyOnly
            ? t("invoice:actions.resend_accounting_copy")
            : issuanceMode === "accounting_provider"
              ? t(
                  props.accountingIntegrationLoading
                    ? "invoice:actions.checking_accounting_integration"
                    : props.integratedAccountingProvider === "tripletex"
                      ? "invoice:actions.continue_to_tripletex"
                      : "invoice:actions.send_to_accounting",
                )
              : props.isResend
                ? t("common:resend")
                : t("common:send")}
          {props.isLoading && (
            <div className="ml-2">
              <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white" />
            </div>
          )}
        </Button>
      </div>
    </div>
  );
};
